// Requests — swaps waiting on me, everything I've asked for, and a new
// time-off flow. Shift-linked rows resolve their shift lazily so the list
// itself stays one cheap query.
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../auth/AuthProvider'
import { fetchMyRequests, fetchShift, fetchSwapsAwaitingMe } from '../../api/reads'
import { api } from '../../api/mutations'
import { useToast } from '../../components/Toast'
import { EmptyState, ErrorBanner, Modal, Spinner, StatusChip } from '../../components/ui'
import { todayLocal } from '../../lib/time'
import { formatDateLong, formatDateShort, formatTime, formatTimeRange } from '../../lib/format'
import type { RequestState, RequestType, ShiftRequest } from '../../lib/types'

const TYPE_LABELS: Record<RequestType, string> = {
  swap: 'Swap',
  drop: 'Drop shift',
  claim: 'Open-shift claim',
  overtime: 'Overtime',
  timeoff: 'Time off',
}

const WAITING_HINTS: Partial<Record<RequestState, string>> = {
  pending_peer: 'waiting on your swap partner',
  pending_approval: 'waiting on a supervisor',
  pending_overtime: 'waiting on a director (overtime)',
}

const PENDING_STATES: RequestState[] = ['pending_peer', 'pending_approval', 'pending_overtime']

export default function RequestsPage() {
  const { email } = useAuth()
  if (!email) return null
  return <Requests email={email} />
}

/** One-line shift summary, fetched lazily per row. */
function ShiftLine({ shiftId }: { shiftId: string }) {
  const { data, isPending } = useQuery({
    queryKey: ['shift', shiftId],
    queryFn: () => fetchShift(shiftId).catch(() => null),
  })
  if (isPending) return <span className="text-sm text-stone">Loading shift…</span>
  if (!data) return <span className="text-sm text-stone">Shift no longer visible</span>
  return (
    <span className="tnum text-sm text-ink">
      {formatDateShort(data.date)} · {formatTimeRange(data.startTime, data.endTime, data.endsNextDay)} ·{' '}
      {data.positionName}
    </span>
  )
}

function summarizeTimeOff(req: ShiftRequest): string {
  if (!req.startDate || !req.endDate) return ''
  const range =
    req.startDate === req.endDate
      ? formatDateLong(req.startDate)
      : `${formatDateShort(req.startDate)} – ${formatDateShort(req.endDate)}`
  return req.partial ? `${range}, ${formatTime(req.partial.startTime)}–${formatTime(req.partial.endTime)}` : range
}

function Requests({ email }: { email: string }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [respondingId, setRespondingId] = useState<string | null>(null)
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  const [timeOffOpen, setTimeOffOpen] = useState(false)

  const awaitingQuery = useQuery({
    queryKey: ['requests', 'peer', email],
    queryFn: () => fetchSwapsAwaitingMe(email),
  })
  const mineQuery = useQuery({
    queryKey: ['requests', 'mine', email],
    queryFn: () => fetchMyRequests(email),
  })

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['requests', 'peer', email] }),
      queryClient.invalidateQueries({ queryKey: ['requests', 'mine', email] }),
      queryClient.invalidateQueries({ queryKey: ['myShifts', email] }),
    ])
  }

  const respond = async (req: ShiftRequest, accept: boolean) => {
    setRespondingId(req.id)
    try {
      await api.respondSwap({ requestId: req.id, accept })
      await invalidateAll()
      toast(accept ? 'Swap accepted — sent on for approval.' : 'Swap declined.', 'success')
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not respond to the swap.', 'error')
    } finally {
      setRespondingId(null)
    }
  }

  const cancel = async (req: ShiftRequest) => {
    setCancellingId(req.id)
    try {
      await api.cancelRequest({ requestId: req.id })
      await invalidateAll()
      toast('Request cancelled.', 'success')
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not cancel the request.', 'error')
    } finally {
      setCancellingId(null)
    }
  }

  const today = todayLocal()
  const canCancel = (req: ShiftRequest): boolean =>
    PENDING_STATES.includes(req.state) ||
    (req.type === 'timeoff' && req.state === 'approved' && !!req.startDate && req.startDate > today)

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-bold text-interlochen">Requests</h1>
        <button className="btn-primary" onClick={() => setTimeOffOpen(true)}>
          New time-off request
        </button>
      </div>

      <section aria-label="Waiting on you">
        <h2 className="section-label mb-2">Waiting on you</h2>
        {awaitingQuery.isPending ? (
          <Spinner label="Checking for swaps…" />
        ) : awaitingQuery.isError ? (
          <ErrorBanner message="Couldn't load swaps waiting on you. Try again." />
        ) : awaitingQuery.data.length === 0 ? (
          <p className="text-sm text-stone">No one is waiting on you right now.</p>
        ) : (
          <ul className="space-y-3">
            {awaitingQuery.data.map((req) => (
              <li key={req.id} className="card space-y-2">
                <p className="font-semibold text-ink">{req.requesterName} wants to trade shifts with you</p>
                <div className="space-y-1">
                  {req.shiftId && (
                    <p>
                      <span className="section-label mr-2">Their shift</span>
                      <ShiftLine shiftId={req.shiftId} />
                    </p>
                  )}
                  {req.counterpartyShiftId && (
                    <p>
                      <span className="section-label mr-2">Your shift</span>
                      <ShiftLine shiftId={req.counterpartyShiftId} />
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    className="btn-primary flex-1 sm:flex-none"
                    onClick={() => void respond(req, true)}
                    disabled={respondingId === req.id}
                  >
                    Accept
                  </button>
                  <button
                    className="btn-secondary flex-1 sm:flex-none"
                    onClick={() => void respond(req, false)}
                    disabled={respondingId === req.id}
                  >
                    Decline
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="My requests">
        <h2 className="section-label mb-2">My requests</h2>
        {mineQuery.isPending ? (
          <Spinner label="Loading your requests…" />
        ) : mineQuery.isError ? (
          <ErrorBanner message="Couldn't load your requests. Try again." />
        ) : mineQuery.data.length === 0 ? (
          <EmptyState title="No requests yet" hint="Swaps, drops, claims, and time off you ask for land here." />
        ) : (
          <ul className="space-y-2">
            {mineQuery.data.map((req) => {
              const hint = WAITING_HINTS[req.state]
              return (
                <li key={req.id} className="card space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-ink">{TYPE_LABELS[req.type]}</span>
                    <StatusChip status={req.state} />
                    <span className="tnum ml-auto text-xs text-stone">{formatDateShort(req.createdAt)}</span>
                  </div>
                  {req.type === 'timeoff' ? (
                    <p className="tnum text-sm text-ink">{summarizeTimeOff(req)}</p>
                  ) : req.shiftId ? (
                    <p>
                      <ShiftLine shiftId={req.shiftId} />
                    </p>
                  ) : null}
                  {req.type === 'swap' && req.counterpartyName && (
                    <p className="text-sm text-stone">with {req.counterpartyName}</p>
                  )}
                  {req.reason && <p className="text-sm text-stone">“{req.reason}”</p>}
                  {hint && <p className="text-xs text-clay">{hint}</p>}
                  {req.decisionNote && <p className="text-sm text-stone">Note: {req.decisionNote}</p>}
                  {canCancel(req) && (
                    <button
                      className="btn-ghost !min-h-[36px] self-start px-2 text-sm"
                      onClick={() => void cancel(req)}
                      disabled={cancellingId === req.id}
                    >
                      {cancellingId === req.id ? 'Cancelling…' : 'Cancel request'}
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <TimeOffModal
        open={timeOffOpen}
        onClose={() => setTimeOffOpen(false)}
        onSubmitted={async () => {
          setTimeOffOpen(false)
          await queryClient.invalidateQueries({ queryKey: ['requests', 'mine', email] })
        }}
      />
    </div>
  )
}

function TimeOffModal({
  open,
  onClose,
  onSubmitted,
}: {
  open: boolean
  onClose: () => void
  onSubmitted: () => Promise<void>
}) {
  const toast = useToast()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [partialStart, setPartialStart] = useState('')
  const [partialEnd, setPartialEnd] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const singleDay = startDate !== '' && startDate === endDate
  const partialTouched = partialStart !== '' || partialEnd !== ''

  const submit = async () => {
    if (!startDate || !endDate) {
      toast('Pick a start and end date.', 'error')
      return
    }
    if (startDate > endDate) {
      toast('The start date must be on or before the end date.', 'error')
      return
    }
    let partial: { startTime: string; endTime: string } | null = null
    if (singleDay && partialTouched) {
      if (!partialStart || !partialEnd) {
        toast('Fill in both partial-day times, or clear them.', 'error')
        return
      }
      if (partialStart >= partialEnd) {
        toast('The partial-day start time must be before the end time.', 'error')
        return
      }
      partial = { startTime: partialStart, endTime: partialEnd }
    }
    setBusy(true)
    try {
      await api.requestTimeOff({ startDate, endDate, reason: reason.trim() || null, partial })
      await onSubmitted()
      setStartDate('')
      setEndDate('')
      setPartialStart('')
      setPartialEnd('')
      setReason('')
      toast('Time-off request sent.', 'success')
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not send the request.', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New time-off request"
      footer={
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose}>
            Never mind
          </button>
          <button className="btn-primary" onClick={() => void submit()} disabled={busy || !startDate || !endDate}>
            {busy ? 'Sending…' : 'Send request'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="timeoff-start" className="section-label mb-1 block">
              First day
            </label>
            <input
              id="timeoff-start"
              type="date"
              className="input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="timeoff-end" className="section-label mb-1 block">
              Last day
            </label>
            <input
              id="timeoff-end"
              type="date"
              className="input"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        {singleDay && (
          <div>
            <p className="section-label mb-1">Partial day (optional)</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="timeoff-partial-start" className="mb-1 block text-sm text-stone">
                  From
                </label>
                <input
                  id="timeoff-partial-start"
                  type="time"
                  className="input"
                  value={partialStart}
                  onChange={(e) => setPartialStart(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="timeoff-partial-end" className="mb-1 block text-sm text-stone">
                  To
                </label>
                <input
                  id="timeoff-partial-end"
                  type="time"
                  className="input"
                  value={partialEnd}
                  onChange={(e) => setPartialEnd(e.target.value)}
                />
              </div>
            </div>
            <p className="mt-1 text-xs text-stone">Leave the times blank for the whole day.</p>
          </div>
        )}
        <div>
          <label htmlFor="timeoff-reason" className="section-label mb-1 block">
            Reason (optional)
          </label>
          <textarea
            id="timeoff-reason"
            className="input min-h-[80px]"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Anything your supervisor should know"
          />
        </div>
      </div>
    </Modal>
  )
}
